// /lists/:listId — one user-created My List: name, description, "X of Y verified"
// progress and every item with poster, season label and coin state. Verified
// items stay in the list and simply show their tier.
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { MyListsService, MY_LIST_ERROR_CODES, getMyListErrorMessage } from '../../services/myListsService';
import { getMediaProgressionState, TIER_COLORS } from '../../utils/mediaProgression';
import { getListItemCta, getListItemStatusLabel, getListItemHref, moveWithinArray } from '../../utils/myListItems';
import { ANALYTICS_EVENTS, trackEvent } from '../../analytics';
import ListNameModal from '../../components/MyLists/ListNameModal';
import ShareListModal from '../../components/MyLists/ShareListModal';
import LoadingSkeleton from '../../components/LoadingSkeleton/LoadingSkeleton';
import styles from './MyListDetail.module.css';

export function ListItemRow({ item, index, total, busy, onMoveUp, onMoveDown, onRemove }) {
    const progression = getMediaProgressionState({ progress: item.progression });
    const tierColor = progression.currentTier ? TIER_COLORS[progression.currentTier] : null;
    const statusLabel = getListItemStatusLabel(progression);
    const ctaLabel = getListItemCta(progression);
    const href = getListItemHref(item);
    const metaParts = [item.mediaType === 'tv' ? 'TV' : 'Movie'];
    if (item.year) metaParts.push(String(item.year));
    if (item.seasonLabel) metaParts.push(item.seasonLabel);

    return (
        <li className={`${styles.item} ${busy ? styles.itemBusy : ''}`}>
            <Link to={href} className={styles.posterWrap} aria-label={item.displayTitle}>
                {item.posterUrl
                    ? <img src={item.posterUrl} alt="" className={styles.poster} loading="lazy" />
                    : <span className={styles.posterFallback}>{item.mediaType === 'tv' ? '📺' : '🎬'}</span>}
                {tierColor && (
                    item.progression?.coinImageUrl
                        ? <img src={item.progression.coinImageUrl} alt={`${statusLabel} coin`} className={styles.coin} style={{ borderColor: tierColor }} />
                        : <span className={styles.coin} style={{ borderColor: tierColor, color: tierColor }} aria-label={`${statusLabel} coin`}>🪙</span>
                )}
            </Link>
            <div className={styles.itemBody}>
                <div className={styles.meta}>{metaParts.join(' · ')}</div>
                <h3 className={styles.itemTitle}>
                    {item.isMediaAvailable ? <Link to={href}>{item.displayTitle}</Link> : <span className={styles.unavailable}>Title unavailable</span>}
                </h3>
                <div className={styles.status} style={{ color: tierColor ?? undefined }}>
                    <span className={styles.dot} style={{ background: tierColor ?? 'currentColor' }} aria-hidden="true" />
                    {statusLabel}
                </div>
                {item.isMediaAvailable && !progression.isComplete && (
                    <Link to={href} className={styles.ctaBtn}>{ctaLabel}</Link>
                )}
            </div>
            <div className={styles.itemActions}>
                <button type="button" className={styles.iconBtn} onClick={onMoveUp} disabled={busy || index === 0} aria-label={`Move ${item.displayTitle} up`} title="Move up">↑</button>
                <button type="button" className={styles.iconBtn} onClick={onMoveDown} disabled={busy || index === total - 1} aria-label={`Move ${item.displayTitle} down`} title="Move down">↓</button>
                <button type="button" className={`${styles.iconBtn} ${styles.removeBtn}`} onClick={onRemove} disabled={busy} aria-label={`Remove ${item.displayTitle} from list`} title="Remove from list">✕</button>
            </div>
        </li>
    );
}

export default function MyListDetail() {
    const { listId } = useParams();
    const navigate = useNavigate();

    const [list, setList] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [renaming, setRenaming] = useState(false);
    const [sharing, setSharing] = useState(false);
    const [busyItemId, setBusyItemId] = useState(null);
    const [actionError, setActionError] = useState('');
    const trackedRef = useRef(false);

    const handleGone = useCallback(() => {
        setError('This list no longer exists.');
        setList(null);
    }, []);

    const load = useCallback(async ({ silent = false } = {}) => {
        if (!silent) setLoading(true);
        setError('');
        try {
            const data = await MyListsService.get(listId);
            const next = data?.list ?? null;
            setList(next);
            if (next && !trackedRef.current) {
                trackedRef.current = true;
                trackEvent(ANALYTICS_EVENTS.MY_LIST_OPEN, { list_item_count: next.itemCount });
                trackEvent(ANALYTICS_EVENTS.MY_LIST_PROGRESS_VIEW, { list_item_count: next.itemCount, verified_count: next.verifiedCount });
            }
        } catch (err) {
            if (err?.code === MY_LIST_ERROR_CODES.LIST_NOT_FOUND || err?.statusCode === 404) handleGone();
            else setError(getMyListErrorMessage(err, "Couldn't load this list."));
        } finally {
            if (!silent) setLoading(false);
        }
    }, [listId, handleGone]);

    useEffect(() => { load(); }, [load]);

    // Coins are earned on other pages — refresh silently when the tab regains focus.
    useEffect(() => {
        const onFocus = () => load({ silent: true });
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, [load]);

    function applyItems(items) {
        setList((prev) => {
            if (!prev) return prev;
            const verified = items.filter((item) => item.isVerified).length;
            return {
                ...prev,
                items,
                itemCount: items.length,
                verifiedCount: verified,
                progress: { verified, total: items.length, isComplete: items.length > 0 && verified === items.length },
            };
        });
    }

    async function handleRename({ name, description, visibility }) {
        const data = await MyListsService.update(listId, { name, description, visibility });
        trackEvent(ANALYTICS_EVENTS.MY_LIST_RENAME, { source_screen: 'my_list_detail' });
        if (visibility && visibility !== list.visibility) {
            trackEvent(ANALYTICS_EVENTS.MY_LIST_VISIBILITY_CHANGED, { previous_visibility: list.visibility, new_visibility: visibility, list_item_count: list.itemCount ?? 0, source_screen: 'edit_list' });
        }
        const updated = data?.list ?? {};
        setList((prev) => (prev ? {
            ...prev,
            name: updated.name ?? name,
            description: updated.description ?? description,
            visibility: updated.visibility ?? visibility ?? prev.visibility,
            shareSlug: updated.shareSlug ?? prev.shareSlug ?? null,
            shareUrl: updated.shareUrl ?? null,
        } : prev));
        setRenaming(false);
    }

    async function handleDelete() {
        if (!window.confirm(`Delete "${list.name}"? Titles stay in your Watchlist and other lists.`)) return;
        try {
            await MyListsService.remove(listId);
            trackEvent(ANALYTICS_EVENTS.MY_LIST_DELETE, { source_screen: 'my_list_detail', list_item_count: list.itemCount });
            navigate('/me');
        } catch (err) {
            if (err?.statusCode === 404) { handleGone(); return; }
            setActionError(getMyListErrorMessage(err));
        }
    }

    async function removeItem(item) {
        const previous = list.items;
        setBusyItemId(item.id);
        setActionError('');
        applyItems(previous.filter((candidate) => candidate.id !== item.id));
        try {
            await MyListsService.removeItem(listId, item.id);
            trackEvent(ANALYTICS_EVENTS.MY_LIST_REMOVE_ITEM, {
                source_screen: 'my_list_detail', media_type: item.mediaType, season_specific: item.seasonNumber != null, list_item_count: previous.length - 1,
            });
        } catch (err) {
            if (err?.code === MY_LIST_ERROR_CODES.LIST_NOT_FOUND) { handleGone(); return; }
            if (err?.code !== MY_LIST_ERROR_CODES.LIST_ITEM_NOT_FOUND) {
                applyItems(previous);
                setActionError(getMyListErrorMessage(err));
            }
        } finally {
            setBusyItemId(null);
        }
    }

    async function moveItem(item, direction) {
        const fromIndex = list.items.findIndex((candidate) => candidate.id === item.id);
        const toIndex = fromIndex + direction;
        if (fromIndex < 0 || toIndex < 0 || toIndex >= list.items.length) return;
        const previous = list.items;
        const reordered = moveWithinArray(previous, fromIndex, toIndex).map((entry, index) => ({ ...entry, sortOrder: index + 1 }));
        setBusyItemId(item.id);
        setActionError('');
        applyItems(reordered);
        try {
            const data = await MyListsService.reorder(listId, reordered.map((entry) => entry.id));
            if (data?.list?.items) applyItems(data.list.items);
            trackEvent(ANALYTICS_EVENTS.MY_LIST_REORDER, { source_screen: 'my_list_detail', list_item_count: reordered.length });
        } catch (err) {
            if (err?.code === MY_LIST_ERROR_CODES.LIST_NOT_FOUND) { handleGone(); return; }
            applyItems(previous);
            setActionError(getMyListErrorMessage(err));
            load({ silent: true });
        } finally {
            setBusyItemId(null);
        }
    }

    if (loading) {
        return <div className={styles.page}><LoadingSkeleton /></div>;
    }

    if (error || !list) {
        return (
            <div className={styles.page}>
                <div className={styles.emptyState}>
                    <h1 className={styles.emptyTitle}>{error === 'This list no longer exists.' ? 'List unavailable' : "Couldn't load this list"}</h1>
                    <p className={styles.emptySubtitle}>{error || 'Please try again.'}</p>
                    <div className={styles.emptyActions}>
                        <Link to="/me" className={styles.primaryBtn}>Back to Watchlist</Link>
                        {error !== 'This list no longer exists.' && (
                            <button type="button" className={styles.secondaryBtn} onClick={() => load()}>Retry</button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    const progress = list.progress ?? { verified: list.verifiedCount ?? 0, total: list.itemCount ?? 0 };
    const pct = progress.total ? Math.round((progress.verified / progress.total) * 100) : 0;

    return (
        <div className={styles.page}>
            <nav className={styles.breadcrumb} aria-label="Breadcrumb">
                <Link to="/me">Watchlist</Link> <span aria-hidden="true">›</span> <span>My Lists</span>
            </nav>

            <header className={styles.header}>
                <div className={styles.headerText}>
                    <h1 className={styles.name}>{list.name}</h1>
                    {list.description && <p className={styles.description}>{list.description}</p>}
                    <p className={styles.count}>
                        {progress.total} title{progress.total === 1 ? '' : 's'} · {list.visibility === 'public' ? '🌎 Public' : '🔒 Private'}
                    </p>
                </div>
                <div className={styles.headerActions}>
                    <button type="button" className={styles.primaryBtn} onClick={() => setSharing(true)}>Share</button>
                    <button type="button" className={styles.secondaryBtn} onClick={() => setRenaming(true)}>Edit</button>
                    <button type="button" className={`${styles.secondaryBtn} ${styles.dangerBtn}`} onClick={handleDelete}>Delete</button>
                </div>
            </header>

            <div className={styles.progressBox} role="group" aria-label="List progress">
                <div className={styles.progressText}>{progress.verified} of {progress.total} verified</div>
                <div className={styles.track} aria-hidden="true">
                    <div className={styles.fill} style={{ width: `${pct}%` }} />
                </div>
            </div>

            {actionError && <div className={styles.error} role="alert">{actionError}</div>}

            {list.items.length === 0 ? (
                <div className={styles.emptyState}>
                    <span className={styles.emptyIcon} aria-hidden="true">🎬</span>
                    <h2 className={styles.emptyTitle}>No titles yet</h2>
                    <p className={styles.emptySubtitle}>Use “Add to List” on any movie or show to add it here.</p>
                    <Link to="/discover" className={styles.primaryBtn}>Browse titles</Link>
                </div>
            ) : (
                <ul className={styles.items}>
                    {list.items.map((item, index) => (
                        <ListItemRow
                            key={item.id}
                            item={item}
                            index={index}
                            total={list.items.length}
                            busy={busyItemId === item.id}
                            onMoveUp={() => moveItem(item, -1)}
                            onMoveDown={() => moveItem(item, 1)}
                            onRemove={() => removeItem(item)}
                        />
                    ))}
                </ul>
            )}

            <ShareListModal
                isOpen={sharing}
                list={list}
                onClose={() => setSharing(false)}
                onListChange={(patch) => setList((prev) => (prev ? { ...prev, ...patch } : prev))}
            />
            <ListNameModal
                isOpen={renaming}
                mode="rename"
                initialName={list.name}
                initialDescription={list.description ?? ''}
                initialVisibility={list.visibility}
                onSubmit={handleRename}
                onClose={() => setRenaming(false)}
            />
        </div>
    );
}
